import React, { useEffect, useState } from "react";
import { api as axios, authFetch } from "../config/apiConfig";
import { API_ORIGIN } from "../config/apiConfig";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../config/routes";
import { printProjectReport } from "../utils/printProjectReport";

// FIX: was hardcoded to "/api" -- see the same fix in
// Step1ProjectInfo.jsx; every other page derives this from API_ORIGIN.
const API_BASE = API_ORIGIN;

// Same step indicator as Step1ProjectInfo.jsx (see the comment there for
// why the other tabs are inert rather than real links).
const WIZARD_STEPS = [
  { key: "project", icon: "ti-file-description", label: "Project", sub: "Info & Overview" },
  { key: "budget", icon: "ti-currency-riyal", label: "Budget", sub: "Amount & Summary" },
  { key: "asset", icon: "ti-building", label: "Asset", sub: "Selection & Element" },
  { key: "element", icon: "ti-list-check", label: "Element", sub: "Main & Sub Element" },
  { key: "estimation", icon: "ti-report-money", label: "Estimation", sub: "Amount & Summary" },
  { key: "basis", icon: "ti-clipboard-text", label: "Basis of Cost", sub: "Assumption & Exclusion" },
  { key: "construction", icon: "ti-hammer", label: "Construction", sub: "Scope" },
];

// Which of the 7 wizard steps this PAGE is. Steps before this one get
// the theme's own light-green "done" tint (.wizard-item-done -- built
// into app.min.css, so this part always rendered correctly); THIS step
// gets the solid brand-green "current" highlight (.wizard-item-current,
// defined in src/styles/themeOverrides.css -- see Layout.jsx for why
// that file, not appLayout.css, is what actually loads it); steps after
// this one stay inert (.disabled).
const CURRENT_STEP_KEY = "asset";
const CURRENT_STEP_INDEX = WIZARD_STEPS.findIndex((s) => s.key === CURRENT_STEP_KEY);

// Per request, Asset Selection is repeatable again: the user can add
// multiple Asset Group + Asset Element pairs via "+ Add Asset Group"
// below, each with its own Asset Elements dropdown (since different rows
// can point at different Asset Groups, which have different Asset
// Element options) and its own Main Element checklist. This had been
// flattened to a single flat Asset Group/Element/Main Element form --
// see the removed comment that used to sit above the old assetGroupID
// state for why -- this reverts that back to a repeatable list.
let nextAssetRowId = 1;
const makeEmptyAssetRow = () => ({
  id: nextAssetRowId++,
  assetGroupID: "",
  assetGroupName: "",
  assetElementID: "",
  assetElementName: "",
  assetElements: [],
  mainElements: []
});

// Rebuilds one repeatable Asset Group/Element row (see makeEmptyAssetRow
// above) from a previously-saved AssetGroup+AssetElement+MainElements
// pair -- used both for restoring a local "step3" draft and for the DB
// fallback prefill below. Fetches that pair's full Asset Elements list
// (for the row's own dropdown) and full Main Elements list, so unchecked
// ones still show instead of just the ones that happened to be checked
// when this was last saved (same fix the single-row version of this page
// already had).
const buildAssetRowFromSaved = async (cId, { assetGroupID, assetGroupName, assetElementID, assetElementName, savedMainElements }) => {
  const row = makeEmptyAssetRow();
  row.assetGroupID = assetGroupID || "";
  row.assetGroupName = assetGroupName || "";
  row.assetElementID = assetElementID || "";
  row.assetElementName = assetElementName || "";

  if (assetGroupID) {
    try {
      const res = await axios.get(`${API_BASE}/get_asset_elements_rate?c_id=${cId}&ag_id=${assetGroupID}`);
      row.assetElements = res.data.data || [];
    } catch {
      row.assetElements = [];
    }
  }

  if (assetGroupID && assetElementID) {
    const savedCheckedIDs = new Set((savedMainElements || []).map((m) => m.ME_ID));
    try {
      const res = await axios.get(`${API_BASE}/get_main_elements_rate?c_id=${cId}&ag_id=${assetGroupID}&ae_id=${assetElementID}`);
      const full = res.data.data || [];
      row.mainElements = full.map((m) => ({ ...m, checked: savedCheckedIDs.has(m.ME_ID) }));
    } catch {
      // Fall back to the old (checked-only) behavior rather than showing
      // nothing if this fetch fails.
      row.mainElements = (savedMainElements || []).map((m) => ({ ...m, checked: true }));
    }
  }

  return row;
};

// Maps each wizard step key to its own dedicated read-only "view" route
// (see the equivalent map in the other StepNReadOnly.jsx files) -- used by
// the wizard tab bar below so every tab is clickable and jumps straight to
// that step's saved-data view, instead of the real editable step page.
const STEP_VIEW_ROUTES = {
  project: ROUTES.COST_ESTIMATION_STEP1_VIEW,
  budget: ROUTES.COST_ESTIMATION_STEP2_VIEW,
  asset: ROUTES.COST_ESTIMATION_STEP3_VIEW,
  element: ROUTES.COST_ESTIMATION_STEP4_VIEW,
  estimation: ROUTES.COST_ESTIMATION_STEP5_VIEW,
  basis: ROUTES.BASIS_OF_COST_VIEW,
  construction: ROUTES.ELEMENT_SCOPE_PAGE1_VIEW
};

function Step3AssetSelectionReadOnly() {
  const navigate = useNavigate();

  const [step1, setStep1] = useState({});
  const [projectName, setProjectName] = useState("");
  const [opco, setOpco] = useState("");

  // Standardized alert presentation (matches Step1ProjectInfo.jsx /
  // Step2RiskInflation.jsx / Step4SubElement.jsx): validation/server
  // errors render as an inline red "alert-danger" banner right below the
  // wizard tabs instead of a native alert() popup. This page has no
  // success alert to convert -- it always navigated silently on save,
  // and per the "no unnecessary alert" requirement that stays unchanged.
  const [serverError, setServerError] = useState(null);

  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");

  // Operating Company and Project Name are now pulled from Rate_Card
  // itself (SELECT DISTINCT OpCo / ProjectName FROM Rate_Card), same as
  // Data Year already was (SELECT DISTINCT RateYear FROM Rate_Card) --
  // per request. Still pre-filled from the project's own OpCo/name below
  // for continuity, but rendered as selectable dropdowns sourced from
  // these two lists instead of a fixed read-only display.
  const [opcoOptions, setOpcoOptions] = useState([]);
  const [projectNameOptions, setProjectNameOptions] = useState([]);

  const [assetGroups, setAssetGroups] = useState([]);

  // Per request, Asset Selection is repeatable again: the user can add
  // multiple Asset Group + Asset Element pairs via "+ Add Asset Group"
  // below, each with its own Asset Elements list (assetElements) and Main
  // Element checklist (mainElements). See makeEmptyAssetRow above.
  const [assetRows, setAssetRows] = useState([makeEmptyAssetRow()]);

  // Per request, the duplicate Asset Group + Asset Element warning shows
  // inside the offending row's own card (right where the user is looking)
  // instead of the shared top-of-page serverError banner -- keyed by row
  // id since more than one row could theoretically hit this at once.
  const [rowErrors, setRowErrors] = useState({});

  useEffect(() => {
    const s1 = {
      projectID: localStorage.getItem("projectID"),
      projectName: localStorage.getItem("projectName"),
      operatingCompany: localStorage.getItem("operatingCompany"),
      C_ID: localStorage.getItem("C_ID")
    };

    setStep1(s1);

    setProjectName(s1.projectName || "");

    // s1.operatingCompany is the OpCo_ID Step1 saved (localStorage
    // "operatingCompany"), not its name -- show the id as a fallback
    // while the real name loads, then swap it in once
    // GET /opco/<id> (SELECT OpCo_Name FROM OpCo WHERE OpCo_ID = ?)
    // resolves it.
    setOpco(s1.operatingCompany || "");
    if (s1.operatingCompany) {
      axios
        .get(`${API_BASE}/opco/${s1.operatingCompany}`)
        .then((res) => {
          if (res.data?.success && res.data?.OpCo_Name) {
            setOpco(res.data.OpCo_Name);
          }
        })
        .catch(() => {
          // Leave the id showing rather than blanking the field --
          // better a value the user can recognize as "off" than nothing.
        });
    }

    // Resume a saved draft -- restores every previously-added Asset
    // Group/Element pair as its own row (was: unwrapped a single "row"
    // back into flat fields, from when this page only ever saved one
    // pair -- now builds one assetRow per saved pair via
    // buildAssetRowFromSaved instead, since there can be more than one
    // again).
    const s3 = JSON.parse(localStorage.getItem("step3"));
    if (s3) {
      setSelectedYear(s3.dataYears?.[0] || "");

      const savedPairs = (s3.rows || []).flatMap((row) =>
        (row.assetElementsData || []).map((ae) => ({
          assetGroupID: row.assetGroupID,
          assetGroupName: row.assetGroupName,
          assetElementID: ae.assetElementID,
          assetElementName: ae.assetElementName,
          savedMainElements: ae.mainElements || []
        }))
      );

      if (savedPairs.length) {
        Promise.all(savedPairs.map((p) => buildAssetRowFromSaved(s1.C_ID, p))).then(setAssetRows);
      }
    } else if (s1.projectID) {
      // No local draft -- prefill from the database instead (e.g.
      // re-opening an already-saved project's Step 3 on a different
      // browser, or after the local draft was cleared). Previously this
      // page had NO database fallback at all, so every field just stayed
      // on its blank default in that case even though the real Asset
      // Group / Asset Element / Main Element selection was already saved.
      // get_project_asset_selection (updated alongside this change) reads
      // back Project_Asset_Selection, the same table
      // save_project_asset_selection writes to on Next -- now returns one
      // entry per saved AssetGroup+AssetElement pair (data.rows), not
      // just a single flat pair.
      axios
        .get(`${API_BASE}/get_project_asset_selection?projectId=${encodeURIComponent(s1.projectID)}`)
        .then((res) => {
          const d = res.data?.data;
          if (!res.data?.success || !d) return;

          if (d.dataYear) setSelectedYear(d.dataYear);

          const savedPairs = (d.rows || []).map((r) => ({
            assetGroupID: r.assetGroupID,
            assetGroupName: r.assetGroupName,
            assetElementID: r.assetElementID,
            assetElementName: r.assetElementName,
            savedMainElements: r.mainElements || []
          }));

          if (savedPairs.length) {
            Promise.all(savedPairs.map((p) => buildAssetRowFromSaved(s1.C_ID, p))).then(setAssetRows);
          }
        })
        .catch((err) => console.error("Failed to load saved asset selection for prefill:", err));
    }

    axios.get(`${API_BASE}/get_rate_years`)
      .then((res) => {
        const list = res.data.data || [];
        setYears(list);
        // Data Year is now a read-only display, not a dropdown the user
        // picks from -- for a brand-new project with no saved "step3"
        // draft (so selectedYear is still "" from state init above),
        // default it to the first distinct year Rate_Card has so the
        // field never shows blank.
        setSelectedYear((prev) => prev || list[0] || "");
      });

    axios.get(`${API_BASE}/get_rate_opcos`)
      .then((res) => setOpcoOptions(res.data.data || []));

    axios.get(`${API_BASE}/get_rate_projects`)
      .then((res) => setProjectNameOptions(res.data.data || []));

    axios.get(`${API_BASE}/get_asset_groups_rate?c_id=${s1.C_ID}`)
      .then((res) => setAssetGroups(res.data.data));
  }, []);

  // Picking a new Asset Group invalidates whatever Asset Element / Main
  // Element that row already had selected -- same reset behavior the old
  // single-row selectAssetGroup had, just scoped to one row now that
  // there can be more than one.
  const selectAssetGroupForRow = (rowId, agID) => {
    const found = assetGroups.find((g) => g.AG_ID === agID);
    setAssetRows((prev) =>
      prev.map((r) =>
        r.id === rowId
          ? { ...r, assetGroupID: agID, assetGroupName: found ? found.Asset_Group : "", assetElementID: "", assetElementName: "", assetElements: [], mainElements: [] }
          : r
      )
    );

    // Changing the Asset Group clears this row's Asset Element too, so
    // whatever duplicate-pair warning was showing no longer applies.
    setRowErrors((prev) => {
      if (!(rowId in prev)) return prev;
      const next = { ...prev };
      delete next[rowId];
      return next;
    });

    if (!agID) return;

    axios.get(`${API_BASE}/get_asset_elements_rate?c_id=${step1.C_ID}&ag_id=${agID}`)
      .then((res) => {
        setAssetRows((prev) =>
          prev.map((r) => (r.id === rowId ? { ...r, assetElements: res.data.data || [] } : r))
        );
      });
  };

  // Asset Element is a plain single-select dropdown (per request -- was a
  // closed multi-select checkbox dropdown before that). Picking a value
  // loads that row's Main Elements fresh, replacing whatever list was
  // showing before, scoped to just this row.
  const selectAssetElementForRow = (rowId, aeID) => {
    // FIX: agID used to be captured as a side effect inside the
    // setAssetRows updater below (`agID = r.assetGroupID`), but React
    // doesn't guarantee that updater runs before the axios.get call right
    // after it -- so the request often fired with agID still "", sending
    // ag_id= empty to /get_main_elements_rate and silently coming back
    // with no rows ("No main elements found for this Asset Element." even
    // though a valid Asset Group + Asset Element were selected). Reading
    // agID straight from the current assetRows state (already set when
    // this row's Asset Group was picked) avoids the race entirely.
    const currentRow = assetRows.find((r) => r.id === rowId);
    const agID = currentRow ? currentRow.assetGroupID : "";

    // Per request: block picking the same Asset Group + Asset Element
    // combination on more than one row -- surfaced inline inside THIS
    // row's own card (rowErrors, keyed by rowId) rather than the shared
    // top-of-page serverError banner, since the user is looking at this
    // row when it happens. This row's Asset Element is left unset rather
    // than silently letting two rows save the identical pair.
    if (aeID && agID) {
      const isDuplicate = assetRows.some(
        (r) => r.id !== rowId && r.assetGroupID === agID && r.assetElementID === aeID
      );
      if (isDuplicate) {
        setRowErrors((prev) => ({
          ...prev,
          [rowId]: "This Asset Group and Asset Element combination has already been added below. Please choose a different Asset Element, or edit the existing row instead."
        }));
        return;
      }
    }

    setRowErrors((prev) => {
      if (!(rowId in prev)) return prev;
      const next = { ...prev };
      delete next[rowId];
      return next;
    });

    setAssetRows((prev) =>
      prev.map((r) => {
        if (r.id !== rowId) return r;
        const found = r.assetElements.find((a) => a.AE_ID === aeID);
        return { ...r, assetElementID: aeID, assetElementName: found ? found.Asset_Element : "", mainElements: [] };
      })
    );

    if (!aeID) return;

    axios
      .get(`${API_BASE}/get_main_elements_rate?c_id=${step1.C_ID}&ag_id=${agID}&ae_id=${aeID}`)
      .then((res) => {
        const withChecked = (res.data.data || []).map((m) => ({ ...m, checked: true }));
        setAssetRows((prev) =>
          prev.map((r) => (r.id === rowId ? { ...r, mainElements: withChecked } : r))
        );
      });
  };

  // FIX: this used to match on m.ME_ID -- but the Main Elements returned
  // for a row can share the same ME_ID (the master Main_Element list is
  // reused across different Asset Group/Element pairs, and two rows'/one
  // row's items aren't guaranteed distinct IDs), so unchecking ONE item
  // whose ME_ID matched more than one entry in the row flipped every
  // matching entry at once -- symptom reported: unchecking a single box
  // in a row unchecked every box in that row. Matching by the item's
  // position in the row's own array instead makes this immune to
  // duplicate/mismatched IDs -- it can only ever affect the one checkbox
  // actually clicked.
  const toggleMainElementForRow = (rowId, idx) => {
    setAssetRows((prev) =>
      prev.map((r) =>
        r.id === rowId
          ? { ...r, mainElements: r.mainElements.map((m, i) => (i === idx ? { ...m, checked: !m.checked } : m)) }
          : r
      )
    );
  };

  // "Select All" convenience for one row's Main Element checklist.
  const toggleSelectAllMainElementsForRow = (rowId) => {
    setAssetRows((prev) =>
      prev.map((r) => {
        if (r.id !== rowId) return r;
        const allChecked = r.mainElements.length > 0 && r.mainElements.every((m) => m.checked);
        return { ...r, mainElements: r.mainElements.map((m) => ({ ...m, checked: !allChecked })) };
      })
    );
  };

  const addCustomMainElementForRow = (rowId) => {
    const name = window.prompt("Enter a name for the new Main Element:");
    if (!name || !name.trim()) return;
    setAssetRows((prev) =>
      prev.map((r) =>
        r.id === rowId
          ? {
              ...r,
              mainElements: [
                ...r.mainElements,
                { ME_ID: `custom-${r.mainElements.length}-${name.trim()}`, Main_Element: name.trim(), checked: true, custom: true }
              ]
            }
          : r
      )
    );
  };

  // "+ Add Asset Group" -- appends a new empty Asset Group/Element/Main
  // Element row below the existing ones, per request.
  const addAssetRow = () => {
    setAssetRows((prev) => [...prev, makeEmptyAssetRow()]);
  };

  // Removes one row. Always keeps at least one row on the page so Asset
  // Selection is never left with nothing to fill in.
  const removeAssetRow = (rowId) => {
    setAssetRows((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== rowId) : prev));
    setRowErrors((prev) => {
      if (!(rowId in prev)) return prev;
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
  };

  const goNext = async () => {
    setServerError(null);

    // FIX: Next used to save and move on even with no Asset Group / Asset
    // Element picked -- required for every row now that there can be more
    // than one, not just the single flat pair -- so block here with the
    // same inline alert-danger banner every other validation on this page
    // uses, rather than a native alert() popup.
    if (assetRows.length === 0 || assetRows.some((r) => !r.assetGroupID || !r.assetElementID)) {
      setServerError("Please select an Asset Group and Asset Element for every row before continuing.");
      return;
    }

    // Defensive duplicate check -- selectAssetElementForRow already blocks
    // picking the same Asset Group + Asset Element pair on more than one
    // row as it happens, but a restored "step3" draft could in theory
    // still contain a duplicate pair saved before that guard existed, so
    // catch it here too rather than letting Next silently save it twice.
    const seenPairs = new Set();
    const hasDuplicatePair = assetRows.some((r) => {
      const key = `${r.assetGroupID}::${r.assetElementID}`;
      if (seenPairs.has(key)) return true;
      seenPairs.add(key);
      return false;
    });
    if (hasDuplicatePair) {
      setServerError("The same Asset Group and Asset Element combination has been added more than once. Please remove the duplicate row before continuing.");
      return;
    }

    // Any Main Element the user typed in via "+ Add Main Element" only
    // exists in this page's local state so far, tagged with a fake
    // "custom-..." ME_ID (see addCustomMainElementForRow above). Per
    // request, insert those into the real Main_Element master table now
    // -- on Next -- via POST /main-element, for every row, so
    // /save_project_asset_selection below writes
    // Project_Asset_Selection.MainElementID as a real, DB-assigned id
    // instead of the placeholder string.
    let resolvedRows = assetRows;

    if (assetRows.some((r) => r.mainElements.some((m) => m.custom))) {
      try {
        resolvedRows = await Promise.all(
          assetRows.map(async (r) => {
            if (!r.mainElements.some((m) => m.custom)) return r;

            const resolvedMainElements = await Promise.all(
              r.mainElements.map(async (m) => {
                if (!m.custom) return m;

                const res = await axios.post(`${API_BASE}/main-element`, {
                  Main_Element: m.Main_Element
                });

                return res.data?.success && res.data?.ME_ID
                  ? { ...m, ME_ID: res.data.ME_ID, custom: false }
                  : m;
              })
            );

            return { ...r, mainElements: resolvedMainElements };
          })
        );
        setAssetRows(resolvedRows);
      } catch (err) {
        setServerError(
          err.response?.data?.message || err.response?.data?.error || "Error saving new Main Element."
        );
        return;
      }
    }

    const payload = {
      projectID: step1.projectID,
      projectName: projectName,
      operatingCompany: opco,
      C_ID: step1.C_ID,
      dataYears: [selectedYear],   // API expects array
      // One "row" per Asset Group/Element pair the user added -- the
      // backend loop (for row in rows / for ae in row.assetElementsData /
      // for me in ae.mainElements) already supported this shape; this
      // page just used to only ever send exactly one. FIX carried over:
      // only CHECKED Main Elements carry through to Step4/the saved
      // selection, not every one in a row's list regardless of checkbox
      // state.
      rows: resolvedRows.map((r) => ({
        assetGroupID: r.assetGroupID,
        assetGroupName: r.assetGroupName,
        assetElementsData: [
          {
            assetElementID: r.assetElementID,
            assetElementName: r.assetElementName,
            mainElements: r.mainElements.filter((m) => m.checked)
          }
        ]
      }))
    };

    axios.post(`${API_BASE}/save_project_asset_selection`, payload)
      .then(() => {
        localStorage.setItem("step3", JSON.stringify(payload));
        navigate(ROUTES.COST_ESTIMATION_STEP4);
      })
      .catch((err) => {
        setServerError(
          err.response?.data?.message || err.response?.data?.error || "Error saving Asset Selection."
        );
      });
  };

  // Per request: once a project has been through the full wizard and
  // Finish has been clicked, every read-only step page for it gets a
  // "Print Report" / "View Dashboard" shortcut in the top-right corner,
  // below the breadcrumb -- no need to jump back to the Estimation
  // Summary page just to print the report or check the portfolio
  // Dashboard. Print Report reuses printProjectReport.js's self-contained
  // generator (the same one ProjectList.jsx's "Estimated" column icon
  // already calls) -- given just a projectId, it fetches everything
  // itself and opens the report exactly the same way.
  const [printReportLoading, setPrintReportLoading] = useState(false);
  const handlePrintReport = async () => {
    const projectId = localStorage.getItem("projectID");
    if (!projectId) return;
    setPrintReportLoading(true);
    try {
      await printProjectReport({ authFetch, API_BASE, projectId });
    } catch (err) {
      console.error("Failed to generate report", err);
    } finally {
      setPrintReportLoading(false);
    }
  };

  return (
    <>
      {/* Was a plain "Start Cost Estimation" title -- replaced with the
          actual project name in a white block, same as Step2RiskInflation.jsx
          / Step4SubElement.jsx / Step5Final.jsx. Uses step1.projectName
          (the loaded value) rather than the `projectName` state below --
          that one is bound to this page's own editable dropdown, not a
          fixed page-title display. */}
      <div className="page-title-head d-flex align-items-center flex-wrap gap-2">
        <div className="flex-grow-1">
          {/* Per request, this is now a breadcrumb trail instead of a
              plain bordered/uppercase title: "Cost Estimation / Asset /
              Project : {name}", each non-current segment (and the "/"
              separators) in #788ca5 at 14px, normal case, no underline
              -- matching the reference design. The current segment
              ("Project : {name}") stays semibold in the app's brand
              teal so it still reads as the active/current item, same
              idea as the reference's blue "Portfolio" segment. */}
          <nav aria-label="breadcrumb" className="d-inline-block">
            <span style={{ fontSize: 16, color: "#02bc9c" }}>Cost Estimation</span>
            <span style={{ fontSize: 16, color: "#02bc9c" }} className="mx-2">/</span>
            <span style={{ fontSize: 16, color: "#02bc9c" }}>Project</span>
            <span style={{ fontSize: 16, color: "#02bc9c" }} className="mx-2">/</span>
            <span className="fw-semibold" style={{ fontSize: 16, color: "#02bc9c" }}>
              {step1.projectName || "—"}
            </span>
          </nav>
        </div>

        {/* Per request: "Print Report" / "View Dashboard" shortcut, top
            right corner below the header, same on every read-only wizard
            step page for a finished project. */}
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <button
            type="button"
            className="btn btn-sm text-white px-3 py-2 fw-semibold d-inline-flex align-items-center gap-2"
            style={{ backgroundColor: "#124d52", borderRadius: 2 }}
            onClick={handlePrintReport}
            disabled={printReportLoading}
          >
            <i className="ti ti-printer fs-5" />
            {printReportLoading ? "Preparing..." : "Print Report"}
          </button>
          <button
            type="button"
            className="btn btn-sm px-3 py-2 fw-semibold d-inline-flex align-items-center gap-2"
            style={{ backgroundColor: "#02bc9c", color: "#fff", borderRadius: 2 }}
            onClick={() => navigate(ROUTES.DASHBOARD)}
          >
            <i className="ti ti-dashboard fs-5" />
            View Dashboard
          </button>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              {/* Wizard progress bar -- see the note above WIZARD_STEPS in
                  Step1ProjectInfo.jsx for why this is a fixed percentage
                  per page instead of the theme's JS-driven one. */}
              <div className="progress mb-4" style={{ height: 6 }}>
                <div className="progress-bar wizard-progress-bar" style={{ width: "100%" }} />
              </div>

              <ul className="nav nav-tabs wizard-tabs w-100 row g-2 border-0" role="tablist">
                {WIZARD_STEPS.map((step, idx) => (
                  <li className="nav-item col-12 col-lg" key={step.key}>
                    <span
                      className="nav-link h-100 wizard-item-done"
                      role="button"
                      tabIndex={0}
                      style={{ cursor: "pointer" }}
                      onClick={() => navigate(STEP_VIEW_ROUTES[step.key])}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          navigate(STEP_VIEW_ROUTES[step.key]);
                        }
                      }}
                    >
                      <span className="d-flex align-items-center">
                        {step.key === "budget" ? (
                          <svg
                            className="flex-shrink-0"
                            style={{ width: "32px", height: "32px" }}
                            viewBox="0 0 917 1025"
                            fill="currentColor"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path fillRule="evenodd" d="M 916 834 L 570 908 L 550 964 L 542 1001 L 540 1024 L 884 951 L 893 932 L 904 899 L 912 865 Z M 646 54 L 539 144 L 539 473 L 435 496 L 430 1 L 324 89 L 324 518 L 83 570 L 51 682 L 323 629 L 324 767 L 32 830 L 0 946 L 310 881 L 346 863 L 428 746 L 437 604 L 539 584 L 539 803 L 885 730 L 916 614 L 646 670 L 646 561 L 885 509 L 916 397 L 646 449 Z" />
                          </svg>
                        ) : (
                          <i className={`ti ${step.icon} fs-32 flex-shrink-0`} />
                        )}
                        <span className="flex-grow-1 ms-2 text-truncate">
                          <span className="mb-0 lh-base d-block fw-semibold text-body fs-base">{step.label}</span>
                          <span className="fs-xxs mb-0">{step.sub}</span>
                        </span>
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
              <hr className="mt-3" />

              {serverError && (
                <div className="alert alert-danger" role="alert">
                  {serverError}
                </div>
              )}

              <fieldset disabled style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>

              <h5 className="mb-3 text-green text-uppercase fs-4 fw-bold">Asset Selection</h5>

              {/* Light background band grouping the page-level filters --
                  Data Year, OpCo, Project Name -- these stay singular per
                  project, unlike Asset Group/Element below which are now
                  repeatable rows. */}
              <div className="bg-light bg-opacity-50 rounded p-3 mb-4">
                <div className="row g-3">
                  {/* Per request: Data Year / OpCo / Project Name are read-only
                      display now, not pickable dropdowns. Data Year shows the
                      resolved value for this project (defaulting to the first
                      distinct Rate_Card year when there's no saved draft).
                      OpCo / Project Name show ALL distinct values Rate_Card
                      has for that field (comma-separated), falling back to
                      the project's own value only if Rate_Card has none. */}
                  <div className="col-xl-4 col-md-6">
                    <label className="form-label">Data Year</label>
                    <input type="text" className="form-control bg-light" value={selectedYear || "—"} readOnly />
                  </div>

                  <div className="col-xl-4 col-md-6">
                    <label className="form-label">Operating Company (OpCo)</label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={opcoOptions.length ? opcoOptions.join(", ") : (opco || "—")}
                      readOnly
                    />
                  </div>

                  <div className="col-xl-4 col-md-6">
                    <label className="form-label">Project Name</label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={projectNameOptions.length ? projectNameOptions.join(", ") : (projectName || "—")}
                      readOnly
                    />
                  </div>
                </div>
              </div>

              {/* Per request: Asset Group + Asset Element (and each row's
                  own Main Element checklist) are repeatable now -- one
                  bordered card per row, added via "+ Add Asset Group"
                  below and removable once there's more than one. */}
              <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-3">
                <span className="fw-bold">Asset Group &amp; Asset Element</span>
                <button type="button" className="btn btn-link p-0 fw-semibold text-decoration-none" onClick={addAssetRow}>
                  <i className="ti ti-plus align-middle me-1" />Add Asset Group
                </button>
              </div>

              {assetRows.map((row, rowIdx) => {
                const rowAllChecked = row.mainElements.length > 0 && row.mainElements.every((m) => m.checked);
                return (
                  <div
                    className="border rounded p-3 mb-3"
                    key={row.id}
                    style={rowIdx % 2 === 1 ? { backgroundColor: "#eef0f2" } : undefined}
                  >
                    {assetRows.length > 1 && (
                      <div className="d-flex align-items-center justify-content-end mb-2">
                        <button
                          type="button"
                          className="btn btn-link p-0 text-danger text-decoration-none"
                          onClick={() => removeAssetRow(row.id)}
                        >
                          <i className="ti ti-trash align-middle me-1" />Remove
                        </button>
                      </div>
                    )}

                    <div className="row g-3 mb-3">
                      <div className="col-xl-6 col-md-6">
                        <label className="form-label">Asset Group</label>
                        <select
                          className="form-select"
                          value={row.assetGroupID}
                          onChange={(e) => selectAssetGroupForRow(row.id, e.target.value)}
                        >
                          <option value="">Select</option>
                          {assetGroups.map((g) => (
                            <option key={g.AG_ID} value={g.AG_ID}>{g.Asset_Group}</option>
                          ))}
                        </select>
                      </div>

                      <div className="col-xl-6 col-md-6">
                        <label className="form-label">Asset Element</label>
                        <select
                          className="form-select"
                          value={row.assetElementID}
                          onChange={(e) => selectAssetElementForRow(row.id, e.target.value)}
                          disabled={!row.assetGroupID}
                        >
                          <option value="">Select</option>
                          {row.assetElements.map((a) => (
                            <option key={a.AE_ID} value={a.AE_ID}>{a.Asset_Element}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {rowErrors[row.id] && (
                      <div className="alert alert-danger py-2 px-3 mb-3" role="alert">
                        {rowErrors[row.id]}
                      </div>
                    )}

                    <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-3">
                      <span className="fw-bold">Main Element</span>
                      <button
                        type="button"
                        className="btn btn-link p-0 fw-semibold text-decoration-none"
                        onClick={() => addCustomMainElementForRow(row.id)}
                      >
                        <i className="ti ti-plus align-middle me-1" />Add Main Element
                      </button>
                    </div>

                    {row.mainElements.length === 0 ? (
                      <div className="text-muted fst-italic mb-0">
                        {row.assetElementID
                          ? "No main elements found for this Asset Element."
                          : "Select an Asset Group and Asset Element to see Main Elements."}
                      </div>
                    ) : (
                      <div className="border rounded mb-0">
                        <div className="border-bottom bg-light bg-opacity-50 px-3 py-2">
                          <div className="form-check mb-0">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id={`s3-me-all-${row.id}`}
                              checked={rowAllChecked}
                              onChange={() => toggleSelectAllMainElementsForRow(row.id)}
                            />
                            <label className="form-check-label fw-semibold" htmlFor={`s3-me-all-${row.id}`}>
                              Select All
                            </label>
                          </div>
                        </div>

                        <div className="px-3 py-2" style={{ maxHeight: 320, overflowY: "auto" }}>
                          {row.mainElements.map((m, idx) => (
                            <div className="form-check mb-1" key={`${row.id}-${idx}-${m.ME_ID}`}>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id={`s3-me-${row.id}-${idx}`}
                                checked={m.checked}
                                onChange={() => toggleMainElementForRow(row.id, idx)}
                              />
                              <label className="form-check-label" htmlFor={`s3-me-${row.id}-${idx}`}>
                                {m.Main_Element}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              </fieldset>

              <div className="d-flex justify-content-between mt-3">
                <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(ROUTES.DASHBOARD, { state: { tab: "executive" } })}>
                  &larr; Back to Dashboard
                </button>
                <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(ROUTES.COST_ESTIMATION_SUMMARY)}>
                  &larr; Back to Summary
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Step3AssetSelectionReadOnly;
